import type { TestGroupingItem } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import { useState, useRef, useEffect } from 'react'
import { useContext } from 'use-context-selector'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import Button, { IconButton } from 'components/uiKit/Button'
import Dropdown from 'components/uiKit/Dropdown'
import Tooltip from 'components/uiKit/Dropdown/Tooltip'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'

import TestGroupingContext from '../../TestGroupingContext'
import * as s from './GroupToolbar.module.scss'

interface IAnswerPreviewProps {
  answer: TestGroupingItem
  fallbackColor?: string
}

const AnswerPreview = ({ answer, fallbackColor }: IAnswerPreviewProps) => {
  const imageUrl = useUrlByUsage(answer.image, FileUsageTypeEnum.image)
  const hasImage = !!answer.image?.id

  return (
    <div
      className={s.answerPreview}
      style={{
        backgroundColor: answer.overlay || fallbackColor,
        backgroundImage: hasImage ? `url(${imageUrl})` : undefined,
      }}
    />
  )
}

interface IAnswerInputProps {
  answer: TestGroupingItem
  labelPath: (string | number)[]
  onChangeLabel: (value: ReturnType<typeof textToRtValue>, path: (string | number)[]) => void
}

const AnswerInput = ({ answer, labelPath, onChangeLabel }: IAnswerInputProps) => {
  const externalValue = rtValueToText(answer.label)
  const [localValue, setLocalValue] = useState(externalValue)

  useEffect(() => {
    setLocalValue(externalValue)
  }, [externalValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    onChangeLabel(textToRtValue(newValue), labelPath)
  }

  return (
    <input
      className={s.answerInput}
      onChange={handleChange}
      placeholder={t('elements.grouping.tags.answer')}
      type='text'
      value={localValue}
    />
  )
}

const GroupToolbar = () => {
  const {
    selectedGroupId,
    value,
    mode,
    styles,
    destination,
    onChangeLabel,
    setSelectedGroupId,
    onAddAnswer,
    onDeleteAnswer,
    onDuplicateAnswer,
    onDeleteGroup,
    onDuplicateGroup,
  } = useContext(TestGroupingContext)

  const [isVisibleItems, setIsVisibleItems] = useState(false)
  const addAnswerBtnRef = useRef<HTMLDivElement>(null)
  const lastMouseDownTargetRef = useRef<EventTarget | null>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      lastMouseDownTargetRef.current = e.target
    }
    document.addEventListener('mousedown', handler, { capture: true })
    return () => document.removeEventListener('mousedown', handler, { capture: true })
  }, [])

  const items = value.items
  const isEditor = mode.previewMode === PreviewMode.editor
  const isFill = isEditor && mode.editorMode === AppMode.fill
  const group = items.find((g) => g.value === selectedGroupId)

  if (!selectedGroupId || !isFill || !group) {
    return null
  }

  const answers = group.items || []

  const handleAddAnswer = () => {
    onAddAnswer(selectedGroupId)
    setIsVisibleItems(true)
  }

  const handleDuplicateAnswer = (answerValue: string) => {
    onDuplicateAnswer(selectedGroupId, answerValue)
  }

  const handleDeleteAnswer = (answerValue: string) => {
    onDeleteAnswer(selectedGroupId, answerValue)
  }

  const handleDuplicateGroup = () => {
    onDuplicateGroup(selectedGroupId)
  }

  const handleDeleteGroup = () => {
    onDeleteGroup(selectedGroupId)
    setSelectedGroupId(undefined)
  }

  const onVisibleChange = (visible: boolean) => {
    if (!visible && addAnswerBtnRef.current?.contains(lastMouseDownTargetRef.current as Node)) {
      return
    }
    setIsVisibleItems(visible)
  }

  const groupIndex = items.findIndex((g) => g.value === selectedGroupId)

  const AnswersDropdownContent = (
    <div className={s.answersDropdown}>
      {answers.map((answer, answerIndex) => {
        const labelPath = ['items', groupIndex, 'items', answerIndex, 'label']

        return (
          <div className={s.answerItem} key={answer.value}>
            <AnswerPreview answer={answer} fallbackColor={styles.answer.backgroundColor} />
            <div className={s.answerLabel}>
              <AnswerInput answer={answer} labelPath={labelPath} onChangeLabel={onChangeLabel} />
            </div>
            <div className={s.answerActions}>
              <Tooltip overlay={t('elements.testGrouping.actions.duplicateAnswer')}>
                <IconButton
                  disabled={answers.length >= 10}
                  icon='otherCopy'
                  iconSize={KitSize.XS}
                  name='duplicateAnswer'
                  onClick={() => handleDuplicateAnswer(answer.value)}
                  size={KitSize.XS}
                  styleType='ghost'
                />
              </Tooltip>
              <Tooltip overlay={t('elements.testGrouping.actions.deleteAnswer')}>
                <IconButton
                  icon='trashDeleteBin'
                  iconSize={KitSize.XS}
                  name='deleteAnswer'
                  onClick={() => handleDeleteAnswer(answer.value)}
                  size={KitSize.XS}
                  styleType='ghost'
                />
              </Tooltip>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <AbsolutePortal
      name='groupToolbar'
      placement='bottomLeft'
      recalculatingKey={destination.length}
      translateY='8px'
    >
      <div className={s.toolbar} onClick={(e) => e.stopPropagation()}>
        {!!answers.length && (
          <Dropdown
            offset={[8, -12]}
            onVisibleChange={onVisibleChange}
            overlay={AnswersDropdownContent}
            overlayStyle={{ zIndex: 100 }}
            placement='bottom-start'
            styleType='clear'
            visible={isVisibleItems}
          >
            {() => (
              <Tooltip overlay={t('elements.testGrouping.answerList')}>
                <Button name='showAnswers' size={KitSize.S} styleType='secondary'>
                  {`${answers.length} ${t('elements.testGrouping.answers')}`}
                  <Icon name='textDown' size={KitSize.XS} />
                </Button>
              </Tooltip>
            )}
          </Dropdown>
        )}
        <div className={s.buttonsGap} ref={addAnswerBtnRef}>
          <Tooltip overlay={t('elements.testGrouping.buttons.addAnswer')}>
            <Button
              disabled={(group?.items?.length || 0) >= 10}
              name='addAnswer'
              onClick={handleAddAnswer}
              size={KitSize.S}
              styleType='secondary'
            >
              <Icon name='otherAdd' size={KitSize.S} />
              {t('elements.testGrouping.buttons.addAnswer')}
            </Button>
          </Tooltip>
        </div>
        <div className={s.buttonsGap}>
          <Tooltip overlay={t('elements.testGrouping.actions.duplicateGroup')}>
            <IconButton
              disabled={items.length >= 10}
              icon='otherCopy'
              iconSize={KitSize.XS}
              name='duplicateGroup'
              onClick={handleDuplicateGroup}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('elements.testGrouping.actions.deleteGroup')}>
            <IconButton
              disabled={items.length <= 1}
              icon='trashDeleteBin'
              iconSize={KitSize.XS}
              name='deleteGroup'
              onClick={handleDeleteGroup}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </div>
      </div>
    </AbsolutePortal>
  )
}

export default GroupToolbar
